/* eslint-disable max-len */
import React, { useEffect, useRef } from 'react';
import { Box, Grid, IconButton, Slider, Stack, Typography } from '@mui/material';
import { FaVolumeDown, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';
import CloseIcon from '@mui/icons-material/Close';
import { FaCirclePlay, FaCirclePause } from 'react-icons/fa6';
import { RiForward15Fill, RiReplay15Fill, RiVolumeUpFill } from 'react-icons/ri';
import { IoPlaySkipForward, IoPlaySkipBack } from 'react-icons/io5';
import { MdVolumeUp } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { setIsPlaying, setVolume, setProgress, resetPlayer, playNextEpisode, playPreviousEpisode } from '../../../../../../redux/AudioPlayerSlice';
import { convertMillisecondsToHHMMSS } from '../../../../../../libs/HelperContent';
import useDidMountEffect from '../../../../../../libs/CustomHooks/UseDidMountEffect';
import { updateAudioProgress } from '../../../../../actions/podcast_management/podcast_management';
import { useActivityLogging } from '../../../../../../libs/CustomHooks/useActivityLogging';
import getDeviceInfo from '../../../../../../libs/BrowserDevices/deviceData';

const AudioPlayer = () =>
{
	const dispatch = useDispatch();
	const audioRef = useRef<HTMLAudioElement>(null);
	const playPromiseRef = useRef<Promise<void> | null>(null);
	const playedDurationRef = useRef<number>(0);
	const isAudioCompletedRef = useRef<boolean>(false);
	const audioDurationRef = useRef<number>(0);
	const { os, platform, ip, browser } = getDeviceInfo();

	const { isPlaying, currentEpisode, volume, progress } = useSelector((state: any) => state.audioPlayer);

	const contentId = currentEpisode?.episodeId || '';

	const { startLogging, stopLogging } = useActivityLogging({
		contentId,
		contentType : 'audio'
	});

	// Audio progress tracking function
	const trackAudioProgress = async (currProgress: {
		duration: number;
		completed: boolean;
		audioDuration: number;
	}) =>
	{
		if (!currentEpisode?.id) return;

		try
		{
			// Update audio progress with detailed tracking
			await updateAudioProgress(
				currentEpisode.podcastId,
				{
					episodeId   : currentEpisode.episodeId,
					seasonId    : currentEpisode.seasonId,
					currentTime : Math.ceil(currProgress.duration),
					duration    : Math.ceil(currProgress.audioDuration)
				},
				{
					deviceType : platform,
					browser,
					os,
					ipAddress  : ip || ''
				}
			);
		}
		catch (error)
		{
			console.error('Error tracking audio progress:', error);
		}
		playedDurationRef.current = 0;
	};

	useEffect(() =>
	{
		if (audioRef.current)
		{
			audioRef.current.volume = volume / 100;
		}
	}, [ volume ]);

	useEffect(() =>
	{
		if (!contentId)
		{
			return;
		}

		startLogging();

		return () =>
		{
			stopLogging();
		};
	}, [ contentId, startLogging, stopLogging ]);

	const handlePlay = async () =>
	{
		if (!audioRef.current) return;

		try
		{
			// If there's a pending play promise, wait for it to resolve
			if (playPromiseRef.current)
			{
				await playPromiseRef.current;
			}

			playPromiseRef.current = audioRef.current.play();
			await playPromiseRef.current;
		}
		catch (error)
		{
			dispatch(setIsPlaying(false));
		}
		finally
		{
			playPromiseRef.current = null;
		}
	};

	const handlePause = async () =>
	{
		if (!audioRef.current) return;

		try
		{
			// If there's a pending play promise, wait for it to resolve
			if (playPromiseRef.current)
			{
				await playPromiseRef.current;
			}
			await audioRef.current.pause();
		}
		catch (error)
		{
			console.error('Pause error:', error);
		}
	};

	useDidMountEffect(() =>
	{
		if (audioRef.current)
		{
			if (isPlaying)
			{
				handlePlay();
			}
			else
			{
				handlePause();
			}
		}
	}, [ isPlaying ]);

	useDidMountEffect(() =>
	{
		if (audioRef.current && currentEpisode?.audioUrl)
		{
			// Reset the audio element
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
			audioRef.current.load();

			// Always start playing when a new episode is set
			handlePlay();
		}
		// eslint-disable-next-line
	}, [currentEpisode?.audioUrl, audioRef.current]);

	useEffect(() =>
	{
		return () =>
		{
			// Track final progress
			if (playedDurationRef.current > 0)
			{
				trackAudioProgress({
					duration      : playedDurationRef.current,
					completed     : isAudioCompletedRef.current,
					audioDuration : audioDurationRef.current
				});
				playedDurationRef.current = 0;
			}
		};
	}, [ contentId ]); // Track when episode changes

	const handleVolumeChange = (event: Event, newValue: number | number[]) =>
	{
		dispatch(setVolume(newValue as number));
	};

	// Handle audio duration when metadata is loaded
	const handleLoadedMetadata = () =>
	{
		if (audioRef.current)
		{
			audioDurationRef.current = audioRef.current.duration;
		}
		if (isPlaying)
		{
			handlePlay();
		}
	};

	const handleProgressChange = (event: Event, newValue: number | number[]) =>
	{
		if (audioRef.current)
		{
			const newTime = (newValue as number) * audioRef.current.duration / 100;

			audioRef.current.currentTime = newTime;
			dispatch(setProgress(newValue as number));
		}
	};

	const handleTimeUpdate = () =>
	{
		if (audioRef.current)
		{
			const currProgress = (audioRef.current.currentTime / audioRef.current.duration) * 100;

			playedDurationRef.current = Math.floor(audioRef.current.currentTime);

			dispatch(setProgress(currProgress));
		}
	};

	const togglePlay = () =>
	{
		dispatch(setIsPlaying(!isPlaying));
	};

	const handleNext = () =>
	{
		dispatch(playNextEpisode());
	};

	const handlePrevious = () =>
	{
		dispatch(playPreviousEpisode());
	};

	const handleSkipForward = () =>
	{
		if (audioRef.current)
		{
			audioRef.current.currentTime = Math.min(audioRef.current.currentTime + 15, audioRef.current.duration);
		}
	};

	const handleSkipBackward = () =>
	{
		if (audioRef.current)
		{
			audioRef.current.currentTime = Math.max(audioRef.current.currentTime - 15, 0);
		}
	};

	// Track progress when user closes the player
	const handleClose = async () =>
	{
		await stopLogging();
		dispatch(resetPlayer());
	};

	// Handle audio completion
	const handleAudioEnded = async () =>
	{
		isAudioCompletedRef.current = true;

		// Track progress when audio ends
		trackAudioProgress({
			duration      : playedDurationRef.current,
			completed     : true,
			audioDuration : audioDurationRef.current
		});
		playedDurationRef.current = 0;

		await stopLogging();
		dispatch(setIsPlaying(false));
	};

	if (!currentEpisode) return null;

	return (
		<Box
			sx={{
				width      : '100%',
				maxWidth   : '100%',
				background : 'radial-gradient(13380.48% 146.23% at 7.71% 65.42%, #256EA6 0%, #3FBBE1 100%)',
				padding    : '12px 16px',
				boxShadow  : 3,
				position   : 'absolute',
				zIndex     : 1011,
				bottom     : 0
			}}
		>
			<audio
				ref={audioRef}
				src={currentEpisode.audioUrl}
				onTimeUpdate={handleTimeUpdate}
				onEnded={handleAudioEnded}
				onLoadedMetadata={handleLoadedMetadata}
			/>
			<Grid container alignItems='center' gap={2}>
				<Grid item>
					<Box
						component='img'
						key={currentEpisode.episodeId || currentEpisode.coverImage}
						src={currentEpisode.coverImage}
						width={100}
						height={70}
						sx={{ borderRadius: '8px', objectFit: 'cover' }}
					/>
				</Grid>
				<Grid item>
					<Typography variant='caption' display='block' color='white' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
						{currentEpisode.title}
					</Typography>
					<Typography variant='body1' color='white' fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px' }}>
						{currentEpisode.title}
					</Typography>
					<Box flexDirection={'row'} display={'flex'} alignItems={'center'} gap={1}>
						<Typography variant='body2' color='rgba(255,255,255,0.8)' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
							{currentEpisode.author}
						</Typography>
					</Box>
				</Grid>
				<Grid item xs>
					<Stack flexDirection='column' width='100%' px={2.5}>
						<Grid container spacing={1}>
							<Grid item display={'flex'} alignItems={'center'}>
								<Typography variant='body2' color='white'>
									{/* {convertMillisecondsToHHMMSS(currentEpisode.duration * 1000)} */}
									{audioRef.current?.currentTime && audioRef.current?.currentTime > 0 ? convertMillisecondsToHHMMSS(audioRef.current?.currentTime * 1000) : '0:00'}
								</Typography>
							</Grid>
							<Grid item xs display={'flex'} alignItems={'center'}>
								<Slider
									value={progress}
									onChange={handleProgressChange}
									sx={{
										color                : 'white',
										'& .MuiSlider-track' : { backgroundColor: 'white' },
										'& .MuiSlider-thumb' : { display: 'none' }
									}}
								/>
							</Grid>
						</Grid>
						<Grid item xs={12} display={'flex'} gap={1} justifyContent={'center'}>
							<IconButton color='inherit' onClick={handleSkipBackward}>
								<RiReplay15Fill color='white'/>
							</IconButton>
							<IconButton color='inherit' onClick={handlePrevious}>
								<IoPlaySkipBack color='white' />
							</IconButton>
							<IconButton onClick={togglePlay} color='inherit'>
								{isPlaying
									? <FaCirclePause color='white' fontSize={38} />
									: <FaCirclePlay color='white' fontSize={38} />
								}
							</IconButton>
							<IconButton color='inherit' onClick={handleNext}>
								<IoPlaySkipForward color='white' />
							</IconButton>
							<IconButton color='inherit' onClick={handleSkipForward}>
								<RiForward15Fill color='white' />
							</IconButton>
						</Grid>
					</Stack>
				</Grid>
				<Grid item>
					<Stack flexDirection='column' width='100%' >
						<Box display={'flex'} justifyContent={'flex-end'} sx={{ position: 'absolute', top: 0, right: 0 }}>
							<IconButton color='inherit' onClick={handleClose}>
								<CloseIcon sx={{ color: 'white' }} fontSize='small'/>
							</IconButton>
						</Box>

						<Box display='flex' flexDirection='row' pt={3} alignItems='center'>
							<IconButton color='inherit'>
								{volume === 0
									? (
										<FaVolumeMute color='white' fontSize={20} />
									)
									: volume < 20
										? (
											<FaVolumeDown color='white' fontSize={20} />
										)
										: volume < 40
											? (
												<MdVolumeUp color='white' fontSize={20} />
											)
											: volume < 60
												? (
													<RiVolumeUpFill color='white' fontSize={20} />
												)
												: (
													<FaVolumeUp color='white' fontSize={20} />
												)}
							</IconButton>

							<Slider
								value={volume}
								onChange={handleVolumeChange}
								sx={{
									color                : 'white',
									'& .MuiSlider-track' : { backgroundColor: 'white' },
									'& .MuiSlider-thumb' : { display: 'none' },
									width                : '100px'
								}}
							/>
						</Box>
					</Stack>
				</Grid>
			</Grid>
		</Box>
	);
};

export default AudioPlayer;
